import { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { Typography } from '../typography';
import { Button } from '../button';
import { enqueueSnackbar } from '../snackbar';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { DatePicker } from '../input';
import { Select } from '../select';
import { useCreateInvoiceMutation } from '../../hooks';
import { invoiceTypeOptions, MESSAGES } from '../../const';
import { i18n } from '../../i18n';
import { toIsoDate } from '../../utils';
import { RefreshPageActions } from '../../store';
import { useStore } from '../../context';
import { InvoiceCreateMode } from '../../types';

interface InvoiceCreateModalType {
  minDate: any;
  createMode: InvoiceCreateMode;
  clientYardIds: string[];
  names: string[];
  submitClick: () => void;
  open: () => void;
}

export const invoiceCreateModal: InvoiceCreateModalType = {
  minDate: {},
  createMode: 'AUTO',
  clientYardIds: [],
  names: [],
  submitClick: () => null,
  open: () => null,
};

export const InvoiceCreateModal = () => {
  const { dispatch } = useStore();
  const { mutate, isLoading } = useCreateInvoiceMutation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>('');
  const [periodStart, setPeriodStart] = useState<Moment | null>(null);
  const [periodEnd, setPeriodEnd] = useState<Moment | null>(null);

  useEffect(() => {
    invoiceCreateModal.open = () => {
      setType('');
      setPeriodStart(null);
      setPeriodEnd(null);
      setOpen(true);
    };
  }, []);

  const handleOnClose = () => {
    invoiceCreateModal.minDate = {};
    setOpen(false);
  };

  const handleSubmitClick = () => {
    if (!periodEnd || !type) return;
    if (invoiceCreateModal.createMode === 'MANUAL' && !periodStart) return;

    mutate(
      {
        clientYardIds: invoiceCreateModal.clientYardIds,
        body: {
          type,
          periodStart: periodStart ? toIsoDate(periodStart) : undefined,
          periodEnd: toIsoDate(periodEnd),
        },
      },
      {
        onSuccess: () => {
          handleOnClose();
          enqueueSnackbar(MESSAGES.INVOICE.SUCCESS.CREATED, 'success');
          dispatch({ type: RefreshPageActions.SET_REFRESH_PAGE });
        },
      },
    );
  };

  return (
    <ModalBase id={'invoice-create-modal'} open={open} onClose={handleOnClose}>
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.invoiceCreateTitle')}
        </Typography>
        <Select
          label={i18n.t('label.type')}
          options={invoiceTypeOptions}
          value={type}
          onChange={(e: any) => {
            setType(e.target.value);
            setPeriodEnd(null);
          }}
        />
        <ModalBodyCustomColumns
          columns={invoiceCreateModal.createMode === 'MANUAL' ? '1fr 1fr' : '1fr'}
        >
          {invoiceCreateModal.createMode === 'MANUAL' && (
            <DatePicker
              label={i18n.t('label.periodStart')}
              data-size={'medium'}
              defaultValue={periodStart}
              onChange={(val: any) => setPeriodStart(val)}
              maxDate={periodEnd ? moment(periodEnd) : moment()}
              withoutClearIcon
              disabled={!type}
            />
          )}
          <DatePicker
            label={i18n.t('label.periodEnd')}
            data-size={'medium'}
            defaultValue={periodEnd}
            onChange={(val: any) => setPeriodEnd(val)}
            minDate={
              invoiceCreateModal.createMode === 'MANUAL'
                ? periodStart
                  ? moment(periodStart)
                  : null
                : type && invoiceCreateModal.minDate[type]
                ? moment(invoiceCreateModal.minDate[type]).add(1, 'day')
                : null
            }
            maxDate={moment()}
            withoutClearIcon
            disabled={!type}
          />
        </ModalBodyCustomColumns>

        <ModalBodyCustomColumns columns={'1fr'}>
          {invoiceCreateModal.names.map((name, i) => (
            <Typography key={i} variant={'textM'}>
              {name}
            </Typography>
          ))}
        </ModalBodyCustomColumns>

        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose} disabled={isLoading}>
            {i18n.t('button.cancel')}
          </Button>
          <Button
            size={'medium'}
            data-color={'primary'}
            onClick={handleSubmitClick}
            disabled={
              isLoading ||
              !periodEnd ||
              !type ||
              (invoiceCreateModal.createMode === 'MANUAL' && !periodStart)
            }
          >
            {i18n.t('button.submit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
